<template>
  <div class="flex flex-col lg:flex-row min-h-screen bg-[#f9fafb]">
    <SidebarComponent class="hidden lg:block" />

    <div class="flex-1 flex flex-col w-full">
      <HeaderComponent class="hidden" />

      <main class="relative flex-1 bg-gray-50 min-h-screen">
        <div class="min-h-screen bg-[#f9fafb]">
          <!-- Mobile Header - Sticky -->
          <div
            class="sticky top-0 z-50 flex items-center justify-between p-4 shadow-sm backdrop-blur-md"
          >
            <div class="flex items-center gap-3">
              <router-link
                to="/quiz"
                class="text-gray-800"
              >
                <i class="fas fa-arrow-left text-lg" />
              </router-link>
              <span class="text-lg font-semibold text-gray-900">{{
                quiz?.subjectName || "Biology"
              }}</span>
            </div>
            <div class="flex items-center gap-3">
              <button
                v-if="canArchive"
                type="button"
                class="text-gray-800 disabled:opacity-50"
                :aria-pressed="isArchived"
                :disabled="archiving"
                aria-label="Archive quiz"
                @click="toggleArchived"
              >
                <i
                  v-if="!archiving"
                  :class="[
                    isArchived
                      ? 'fas fa-box-open text-lg'
                      : 'fas fa-box-archive text-lg',
                  ]"
                />
                <i
                  v-else
                  class="fas fa-spinner fa-spin text-lg"
                />
              </button>
              <button
                type="button"
                class="text-gray-800 disabled:opacity-50"
                :disabled="sharing"
                aria-label="Share quiz"
                @click="shareQuiz"
              >
                <i
                  v-if="!sharing"
                  class="fas fa-share-square text-lg"
                />
                <i
                  v-else
                  class="fas fa-spinner fa-spin text-lg"
                />
              </button>
              <button
                type="button"
                class="text-gray-800 disabled:opacity-50"
                :aria-pressed="isSaved"
                :disabled="saving"
                aria-label="Save quiz"
                @click="toggleSaved"
              >
                <i
                  v-if="!saving"
                  :class="[isSaved ? 'fas' : 'far', 'fa-bookmark', 'text-lg']"
                />
                <i
                  v-else
                  class="fas fa-spinner fa-spin text-lg"
                />
              </button>
            </div>
          </div>

          <!-- Cover Image -->
          <div class="px-4 mb-6">
            <div class="relative rounded-2xl overflow-hidden group">
              <img
                :src="quiz?.image || require('@/assets/quiz.png')"
                alt="Quiz Image"
                loading="lazy"
                class="w-full h-[250px] object-cover"
              >
              <div
                v-if="canEditCover"
                class="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition"
              />
              <button
                v-if="canEditCover"
                type="button"
                class="absolute bottom-3 right-3 inline-flex items-center gap-2 rounded-xl bg-white/90 px-3 py-2 text-xs font-semibold text-gray-800 shadow hover:bg-white transition"
                :disabled="updatingCover"
                @click="emit('edit-cover')"
              >
                <i
                  v-if="!updatingCover"
                  class="fas fa-pen"
                />
                <i
                  v-else
                  class="fas fa-spinner fa-spin"
                />
                {{ updatingCover ? "Updating..." : "Change cover" }}
              </button>
            </div>
          </div>

          <!-- Quiz Info Card -->
          <div class="mx-4 mb-6">
            <h1 class="text-2xl font-bold text-gray-900 mb-3">
              {{ quiz?.title || "Additional Mathematics" }}
            </h1>
            <p class="text-base text-gray-600 mb-4 leading-relaxed">
              {{
                quiz?.description ||
                  "-"
              }}
            </p>
          </div>

          <!-- UPGRADE PRO BANNER (conditional dari backend) -->
          <div
            v-if="showUpgradeBanner"
            class="mx-4 mb-6 rounded-2xl bg-gradient-to-r from-[#0B63F6] to-[#1E40AF] px-5 py-5 text-white flex items-center justify-between gap-4 shadow-lg"
          >
            <div class="flex items-center gap-4">
              <div
                class="w-[70px] h-[70px] bg-white rounded-full flex items-center justify-center flex-shrink-0 shadow-md"
              >
                <img
                  src="@/assets/crown.png"
                  class="w-12 h-12"
                  alt="crown"
                >
              </div>
              <div>
                <p class="text-lg font-bold leading-6">
                  Upgrade pro
                </p>
                <p class="text-sm opacity-90 mt-1 leading-5">
                  Upgrade to remove ads, unlimited play and access all game
                </p>
              </div>
            </div>

            <button
              class="shrink-0 bg-[#F59E0B] hover:bg-[#EA8A00] text-white px-4 py-3 rounded-full font-bold text-sm shadow-md"
              @click="goSubscribe"
            >
              Upgrade
            </button>
          </div>

          <!-- Tab Switcher -->
          <div class="mx-4 mb-6">
            <SwitchButtonGroup
              v-model="selectedMenu"
              :options="['Detail', 'Quiz']"
              size="md"
              variant="pill"
            />
          </div>

          <!-- TAB: DETAIL -->
          <div v-if="selectedMenu === 'Detail'">
            <!-- Outcome Section -->
            <div class="mx-4 mb-6 bg-white rounded-2xl p-5 shadow-sm">
              <h2 class="text-xl font-bold text-gray-900 mb-5">
                Outcome
              </h2>

              <div class="grid grid-cols-2 gap-4">
                <div
                  class="flex items-center justify-between p-4 rounded-xl"
                  style="background-color: #f8fafc"
                >
                  <div>
                    <div class="text-sm text-gray-500">
                      Skill level:
                    </div>
                    <div class="font-bold text-gray-900">
                      {{ quiz?.level }}
                    </div>
                  </div>
                  <div class="w-10 h-10 flex items-center justify-center">
                    <img
                      src="@/assets/star.png"
                      alt="Star"
                      class="w-7 h-7"
                    >
                  </div>
                </div>

                <div
                  class="flex items-center justify-between p-4 rounded-xl"
                  style="background-color: #f8fafc"
                >
                  <div>
                    <div class="text-sm text-gray-500">
                      Quizzes
                    </div>
                    <div class="font-bold text-gray-900">
                      {{ quiz?.totalQuiz }}
                    </div>
                  </div>
                  <div class="w-10 h-10 flex items-center justify-center">
                    <img
                      src="@/assets/note.png"
                      alt="Note"
                      class="w-7 h-7"
                    >
                  </div>
                </div>

                <div
                  class="flex items-center justify-between p-4 rounded-xl"
                  style="background-color: #f8fafc"
                >
                  <div>
                    <div class="text-sm text-gray-500">
                      Language:
                    </div>
                    <div class="font-bold text-gray-900">
                      {{ quiz?.language }}
                    </div>
                  </div>
                  <div class="w-10 h-10 flex items-center justify-center">
                    <img
                      src="@/assets/language-circle.png"
                      alt="Language"
                      class="w-7 h-7"
                    >
                  </div>
                </div>

                <div
                  class="flex items-center justify-between p-4 rounded-xl"
                  style="background-color: #f8fafc"
                >
                  <div>
                    <div class="text-sm text-gray-500">
                      Duration:
                    </div>
                    <div class="font-bold text-gray-900">
                      {{ quiz?.timerString }}
                    </div>
                  </div>
                  <div class="w-10 h-10 flex items-center justify-center">
                    <img
                      src="@/assets/timer-start.png"
                      alt="Timer"
                      class="w-7 h-7"
                    >
                  </div>
                </div>
              </div>
            </div>

            <!-- Leaderboard Section -->
            <div class="mx-4 mb-6 bg-white rounded-2xl p-5 shadow-sm">
              <div class="flex justify-between items-center mb-5">
                <h3 class="text-xl font-bold text-gray-900">
                  Leaderboard
                </h3>
                <router-link
                  to="/ranking"
                  class="text-base text-blue-600 font-semibold hover:text-blue-700"
                >
                  View All
                </router-link>
              </div>

              <div class="space-y-4 max-h-72 overflow-y-auto pr-1">
                <div
                  v-for="(user, index) in leaderboard"
                  :key="index"
                  class="flex items-center gap-4"
                >
                  <div class="w-7 h-7 flex items-center justify-center">
                    <img
                      v-if="index < 3"
                      :src="user.badge"
                      :alt="`Rank ${index + 1}`"
                      class="w-7 h-7"
                    >
                  </div>
                  <img
                    :src="user.avatar"
                    :alt="user.name"
                    class="w-10 h-10 rounded-full object-cover"
                  >
                  <div class="flex-1">
                    <p class="font-semibold text-gray-900 text-base">
                      {{ user.name }}
                    </p>
                    <p class="text-sm text-gray-500">
                      {{ user.xp }} XP
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- TAB: QUIZ -->
          <div v-else>
            <!-- Quizzes Section -->
            <div class="mx-4 mb-6 bg-white rounded-2xl p-5 shadow-sm">
              <h2 class="text-xl font-bold text-gray-900 mb-5">
                Quizzes
              </h2>
              <div
                v-for="(item, index) in quizList"
                :key="index"
                class="flex items-start gap-3 p-3 bg-gray-50 rounded-xl mb-3 last:mb-0"
                @click="goToQuiz(item)"
                @keydown.enter="goToQuiz(item)"
                @keydown.space.prevent="goToQuiz(item)"
              >
                <img
                  :src="item.image || require('@/assets/quiz.png')"
                  alt="Quiz"
                  class="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                >
                <div class="flex-1 min-w-0">
                  <h3 class="font-semibold text-gray-900 text-sm leading-tight">
                    {{ item.title || "Additional Mathematics" }}
                  </h3>
                  <p
                    class="text-sm text-gray-400 leading-tight line-clamp-3 mb-1"
                  >
                    {{ item.description || "-" }}
                  </p>
                  <div class="flex justify-between items-start mt-2">
                    <div class="flex flex-col gap-1">
                      <div class="flex items-center gap-1">
                        <img
                          src="@/assets/document.png"
                          alt="Document"
                          class="w-3 h-3"
                        >
                        <span class="text-xs text-gray-500">{{ item.totalQuiz || 5 }} Questions</span>
                      </div>
                      <div class="flex items-center gap-1">
                        <img
                          src="@/assets/user.png"
                          alt="User"
                          class="w-3 h-3"
                        >
                        <span class="text-xs text-gray-500">{{ item.submittedCount || 0 }} Student</span>
                      </div>
                    </div>
                    <div class="flex flex-col items-end gap-1">
                      <div class="flex items-center gap-1">
                        <img
                          src="@/assets/clock.png"
                          alt="Clock"
                          class="w-3 h-3"
                        >
                        <span class="text-xs text-gray-500">{{ item.totalHour }}m</span>
                      </div>
                      <div
                        class="flex items-center gap-1 bg-[#2563EB] text-white px-2 py-1 rounded-md"
                      >
                        <img
                          src="@/assets/coin.png"
                          alt="Coin"
                          class="w-3 h-3"
                        >
                        <span class="text-xs font-semibold">120</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Mobile CTA -->
          <div class="sticky bottom-0 left-0 right-0 p-5 bg-white shadow-lg">
            <router-link
              :to="{
                path: isLogin ? `/quiz-confirmation/${quizId}` : `/login`,
                query: {
                  id: quiz?.id,
                  img: quiz?.imagePath,
                  quizDurationHms: quiz?.timer,
                  quizGuid: quiz?.id,
                  quizTitle: quiz?.title,
                  requireReviewPin: quiz?.requireReviewPin ? '1' : '0',
                },
              }"
              class="w-full block text-center bg-[#2563EB] hover:bg-[#1E40AF] text-white py-4 rounded-xl text-lg font-bold shadow-md"
            >
              {{ isLogin ? "Start Quiz Now" : "Join To Start Quiz" }}
            </router-link>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script setup>
import { defineEmits, defineProps, ref } from "vue";
import SidebarComponent from "@/components/base/SidebarComponent.vue";
import HeaderComponent from "@/components/base/HeaderComponent.vue";
import SwitchButtonGroup from "@/components/base/SwitchButton.vue";

defineProps({
  quiz: Object,
  quizId: [String, Number],
  quizList: Array,
  leaderboard: Array,
  selectedMenu: String,
  showUpgradeBanner: Boolean,
  quizLoading: Boolean,
  goSubscribe: Function,
  goToQuiz: Function,
  toggleSaved: Function,
  shareQuiz: Function,
  isLogin: Boolean,
  isSaved: Boolean,
  saving: Boolean,
  sharing: Boolean,
  canArchive: Boolean,
  toggleArchived: Function,
  isArchived: Boolean,
  archiving: Boolean,
  canEditCover: Boolean,
  updatingCover: Boolean,
});

const emit = defineEmits(["edit-cover"]);
const selectedMenu = ref("Detail");
</script>
